import React, { useState, useEffect } from 'react';
import { 
  ShieldExclamationIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { Link } from 'react-router-dom';
import api from '@/services/api';

interface KeyRisk {
  id: string;
  name: string;
  description: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  probability: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'IDENTIFIED' | 'ASSESSED' | 'MITIGATED' | 'ACCEPTED' | 'CLOSED';
  current_owner_name?: string;
  due_date?: string;
  entity_type?: string;
  entity_name?: string;
  mitigation_steps?: string;
  risk_score?: number;
}

interface KeyRisksWidgetProps {
  tenantId: string;
  limit?: number;
}

export const KeyRisksWidget: React.FC<KeyRisksWidgetProps> = ({ 
  tenantId, 
  limit = 5 
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [risks, setRisks] = useState<KeyRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId && user) {
      loadKeyRisks();
    }
  }, [tenantId, user]);

  const loadKeyRisks = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use centralized API service which handles base URL and authentication
      const response = await api.get(`/risks/key-risks?limit=${limit}`);
      
      // Validate response structure
      const data = response.data;
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format: expected object');
      }

      setRisks(data.data || []);
    } catch (error: any) {
      console.error('Error loading key risks:', error);
      // Extract error message from axios error response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to load key risks';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'text-red-600';
      case 'HIGH':
        return 'text-orange-600';
      case 'MEDIUM':
        return 'text-yellow-600';
      case 'LOW':
        return 'text-green-600';
      default:
        return 'text-[var(--color-secondary-text)]';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
      case 'HIGH':
        return ExclamationTriangleIcon;
      default:
        return ShieldExclamationIcon;
    }
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString();
  };


  if (loading) {
    return (
      <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
        <CardHeader>
          <CardTitle className="text-[var(--color-surface-text)] flex items-center">
            <ShieldExclamationIcon className="h-5 w-5 mr-2" />
            {t('dashboard.keyRisks')}
          </CardTitle>
          <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
            {t('dashboard.keyRisksDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
            <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">{t('common.loading')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
        <CardHeader>
          <CardTitle className="text-[var(--color-surface-text)] flex items-center">
            <ShieldExclamationIcon className="h-5 w-5 mr-2" />
            {t('dashboard.keyRisks')}
          </CardTitle>
          <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
            {t('dashboard.keyRisksDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-red-600">{error}</p>
            <button 
              onClick={loadKeyRisks}
              className="mt-2 text-sm text-[var(--color-accent-text)] hover:underline"
            >
              {t('common.retry')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
      <CardHeader>
        <CardTitle className="text-[var(--color-surface-text)] flex items-center">
          <ShieldExclamationIcon className="h-5 w-5 mr-2" />
          {t('dashboard.keyRisks')}
        </CardTitle>
        <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
          {t('dashboard.keyRisksDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {risks.length === 0 ? (
          <div className="text-center py-4">
            <ShieldExclamationIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
            <p className="text-sm opacity-75">
              {t('dashboard.noKeyRisks')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {risks.map((risk) => {
              const SeverityIcon = getSeverityIcon(risk.severity);
              return (
                <Link
                  key={risk.id}
                  to={`/risk?tab=pool&riskId=${risk.id}`}
                  className="group flex items-center justify-between p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] hover:bg-[var(--color-accent-bg)] hover:bg-opacity-10 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium truncate group-hover:text-[var(--color-accent-text)] transition-colors">
                        {risk.name}
                      </h4>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-surface-bg)] ${getSeverityColor(risk.severity)}`}>
                        <SeverityIcon className="h-2.5 w-2.5 mr-0.5" />
                        {risk.severity}
                      </span>
                    </div>
                    {risk.due_date && (
                      <div className="flex items-center space-x-3 text-xs opacity-75">
                        <span className="flex items-center">
                          <CalendarIcon className="h-2.5 w-2.5 mr-0.5" />
                          {formatDate(risk.due_date)}
                        </span>
                      </div>
                    )}
                  </div>
                  <EyeIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-50 group-hover:opacity-100" />
                </Link>
              );
            })}
          </div>
        )}

        {/* View All Risks Link */}
        <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
          <Link
            to="/risk"
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:bg-opacity-90 rounded-md transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('dashboard.viewAllRisks')}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
